/**
 * Shared, short-TTL accessor for `GET /api/v1/shell/operations/summary`.
 *
 * Two unrelated parts of the app want the same payload at the same moment:
 * the sidebar's live count badges (`components/nav/Menu.vue`) and the
 * operations Overview (`components/Shell/OperationsOverviewBoard.vue`). Icarus
 * re-fetches the entire page tree — sidebar included — on EVERY navigation,
 * so without coordination a visit to `/` would issue the same request twice,
 * and every other navigation once more.
 *
 * This module is that coordination, deliberately as a plain module rather than
 * a pinia store: there is no state to mutate, no persistence, and nothing to
 * devtools-inspect — just "one request per TTL, whoever asks". A store would
 * add ceremony without adding capability.
 *
 * Three guarantees:
 *  1. **Concurrent callers share one in-flight request.** The pending promise
 *     is memoised, not just its result, so the nav and the Overview mounting
 *     in the same tick make one call between them.
 *  2. **A fresh-enough result is returned without a request at all**, which is
 *     what keeps navigation-driven remounts of the sidebar cheap.
 *  3. **A failure is never cached.** The promise is cleared on rejection so
 *     the next caller retries rather than inheriting a dead result — a
 *     transient 502 must not leave the sidebar badge-less until the TTL
 *     expires.
 */
import useApi from '@apexl/icarus-core/src/services/api';

/**
 * 20 seconds. Matches `ServersFleetBoard.vue`'s own poll interval so the
 * sidebar's alert count and the fleet board never visibly disagree, and is
 * short enough that acknowledging an alert updates the badge on the next
 * navigation rather than a minute later.
 */
const TTL_MS = 20000;

let cached = null;
let cachedAt = 0;
let inFlight = null;

/**
 * @param {{force?: boolean}} [options] `force` bypasses the TTL (used by the
 *   Overview's own poll, which wants genuinely live numbers while it is the
 *   visible page).
 * @returns {Promise<object|null>} the summary payload, or null when the
 *   request failed — callers render their own empty/error state rather than
 *   being handed a fabricated zero.
 */
export function fetchOperationsSummary({ force = false } = {}) {
  const fresh = cached !== null && Date.now() - cachedAt < TTL_MS;
  if (fresh && !force) {
    return Promise.resolve(cached);
  }
  if (inFlight) {
    return inFlight;
  }

  const api = useApi();
  inFlight = api.get('/api/v1/shell/operations/summary')
    .then((data) => {
      cached = data || null;
      cachedAt = Date.now();
      return cached;
    })
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/** The last payload without triggering a request — for a first paint that shouldn't flash empty. */
export function peekOperationsSummary() {
  return cached;
}

/**
 * Drop the cache. Called after an action that provably changed the numbers
 * (acknowledging an alert, adding a notification channel), so the next read
 * reflects it immediately instead of up to a TTL later.
 */
export function invalidateOperationsSummary() {
  cached = null;
  cachedAt = 0;
}

/**
 * Resolve a dotted path (`alerts.open`) against a payload. The nav's badge map
 * is declared server-side as dotted paths — see
 * `app\module\shell\Hook\RegisterGoshawkNavGroupsHook::BADGES` — so this is
 * the only piece of interpretation the client needs.
 */
export function readPath(payload, path) {
  if (!payload || typeof path !== 'string') {
    return null;
  }

  return path.split('.').reduce(
    (node, key) => (node !== null && node !== undefined ? node[key] : undefined),
    payload,
  ) ?? null;
}
